import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import { staffAPI } from "../../services/api";
import "./BookingRestrictionsModal.css";

// frontend/src/components/staff/BookingRestrictionsModal.js
//
// BOOKING-RESTRICT-003 — Surface B (Admin block-list management modal).
// Mounted from BookingRestrictionsCard's CTA on /staff/customers/:customerId.
//
// Spec sources (locked):
//   docs/design/booking-restrictions-admin-design.md (Zuri)
//     §4   — modal layout + sections (BLOCKED SUITES / BLOCKED TIME WINDOWS /
//            EFFECTIVE UNTIL / REASON) + add affordances + validation
//     §4.2 — single-record mental model: ONE form, ONE PUT, full-replace
//     §4.8 — MANDATORY confirmation modal on Save (verbatim guardrail copy)
//     §4.9 — Cancel / Escape / close-X / backdrop dirty-state guard
//     §4.10 — destructive Remove-all-restrictions confirmation modal
//     §4.11 — empty-state nudge for first-time-add
//     §4.12 — inactive-suite handling
//     §4.13 + §9 — accessibility (focus trap, focus rings, ARIA)
//     §6 + §7 + §8 — typography / colors / spacing
//     §10.2 — Visual DoD checklist
//   docs/architecture/booking-restrictions-tech.md (Marcus)
//     §3.3 — admin endpoints: GET / PUT (full-replace, idempotent) / DELETE
//     §1 + §2 — half-open interval semantics; cross-midnight authoring rule
//     §6 — Indistinguishability checklist (HARD RULE §)
//
// HARD RULE § — Customer-side ZERO presence (binding):
//   This modal is staff-only. It MUST NEVER be imported into customer-facing
//   pages (MemberProfile, BookSession, member-dashboard, etc.). The PUT body
//   contains the full single-record state per Marcus's full-replace semantics;
//   the response is staff-authenticated and never echoed to the customer.
//
// Production-safety:
//   - Single-revert posture: dropping this PR removes the modal; the backend
//     admin endpoints (Story 001, LIVE in main) continue to function via
//     CLI / Postman path.
//   - Touches no customer-facing component or service helper.

const PHOENIX_TZ = "America/Phoenix";
const REASON_MAX_LENGTH = 500;
const REASON_COUNTER_THRESHOLD = 450;

const SUITE_TYPE_LABELS = {
  sauna: "Sauna",
  redlight: "Red Light",
  red_light: "Red Light",
  red_light_bed: "Red Light",
  massage: "Massage",
  infrared: "Sauna",
};

const DAY_LETTERS = ["S", "M", "T", "W", "T", "F", "S"];
const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Convert a `daysOfWeek` array (0..6, Sunday-first) into a friendly display
 * string per Zuri spec §4.5 + Scenario 11. "Daily" / "Weekdays" / "Weekends"
 * are special-cased; otherwise comma-separated 3-letter abbreviations in
 * week order.
 *
 * Exported for unit-testing the day-list renderer in isolation.
 */
export const formatDaysOfWeek = (days) => {
  if (!Array.isArray(days) || days.length === 0) return "";
  const sorted = [...new Set(days)].sort((a, b) => a - b);
  const isAll = sorted.length === 7 && sorted.every((d, i) => d === i);
  if (isAll) return "Daily";
  const isWeekdays =
    sorted.length === 5 &&
    sorted[0] === 1 &&
    sorted[1] === 2 &&
    sorted[2] === 3 &&
    sorted[3] === 4 &&
    sorted[4] === 5;
  if (isWeekdays) return "Weekdays";
  const isWeekends =
    sorted.length === 2 && sorted[0] === 0 && sorted[1] === 6;
  if (isWeekends) return "Weekends";
  return sorted.map((d) => DAY_ABBR[d]).join(", ");
};

/**
 * Validate a candidate time-window. Returns { valid: bool, error: string }.
 * Surfaces verbatim error copy required by Zuri §4.5 + Scenario 10.
 *
 * Exported for unit-testing.
 */
export const validateTimeWindow = ({ daysOfWeek, startTime, endTime }, existing = []) => {
  if (!Array.isArray(daysOfWeek) || daysOfWeek.length === 0) {
    return { valid: false, error: "Choose at least one day." };
  }
  if (!startTime || !endTime) {
    return { valid: false, error: "Start and end times are required." };
  }
  // Half-open semantics: endTime must be strictly greater than startTime
  // within the same day. Cross-midnight = two separate windows.
  if (endTime <= startTime) {
    return {
      valid: false,
      error: "End time must be after start time. For overnight rules, add two windows.",
    };
  }
  // Duplicate detection per Zuri §4.5
  const isDuplicate = existing.some((win) => {
    if (win.startTime !== startTime || win.endTime !== endTime) return false;
    const a = [...new Set(win.daysOfWeek || [])].sort((x, y) => x - y);
    const b = [...new Set(daysOfWeek)].sort((x, y) => x - y);
    return a.length === b.length && a.every((d, i) => d === b[i]);
  });
  if (isDuplicate) {
    return {
      valid: false,
      error: "This time window is already blocked. Edit or remove the existing one instead.",
    };
  }
  return { valid: true, error: "" };
};

const formatSuiteIdentity = (suite) => {
  if (!suite) return "";
  const typeKey = String(suite.type || "").toLowerCase().replace(/\s+/g, "_");
  const typeLabel = SUITE_TYPE_LABELS[typeKey] || (suite.type || "Suite");
  const number = suite.number != null ? ` ${suite.number}` : "";
  if (suite.name && String(suite.name).trim()) {
    return `${typeLabel}${number} — "${suite.name}"`;
  }
  return `${typeLabel}${number}`.trim();
};

const formatProvenanceTimestamp = (iso) => {
  if (!iso) return null;
  const m = moment.tz(iso, PHOENIX_TZ);
  if (!m.isValid()) return null;
  return m.format("MMM D, YYYY HH:mm");
};

const formatEffectiveUntilDisplay = (iso) => {
  if (!iso) return "";
  const m = moment.tz(iso, PHOENIX_TZ);
  if (!m.isValid()) return "";
  return m.format("MMM D, YYYY");
};

/**
 * BookingRestrictionsModal — Surface B.
 *
 * Single-record full-replace editor for the customer's restriction profile.
 * Renders the editor + nested confirmation modal (Save) + nested destructive
 * confirmation (Remove all). All add/remove operations build local working
 * state; the parent Save commits the whole profile via PUT.
 *
 * @param {object} props
 * @param {boolean} props.isOpen — controls visibility; render-null when false.
 * @param {string} props.customerId — the customer's Mongo _id.
 * @param {object} props.customer — minimum: { firstName, lastName, email }.
 * @param {object|null} [props.bookingRestrictions] — current sub-doc from
 *   the customer-detail response. Null when no profile exists yet.
 * @param {Function} props.onClose — invoked on dismiss (with or without save).
 * @param {Function} [props.onSaved] — invoked after a successful PUT/DELETE
 *   so the parent can refresh the customer-detail query (TanStack-style
 *   invalidation; the staff frontend does not currently use TanStack Query
 *   so the parent re-fetches via its own loader).
 */
const BookingRestrictionsModal = ({
  isOpen,
  customerId,
  customer,
  bookingRestrictions,
  onClose,
  onSaved,
}) => {
  // Working state — single-record per Zuri §4.2
  const [blockedSuiteIds, setBlockedSuiteIds] = useState([]);
  const [blockedTimeWindows, setBlockedTimeWindows] = useState([]);
  const [isIndefinite, setIsIndefinite] = useState(true);
  const [effectiveUntil, setEffectiveUntil] = useState("");
  const [reason, setReason] = useState("");

  // Add-suite affordance state
  const [addSuiteOpen, setAddSuiteOpen] = useState(false);
  const [pendingSuiteId, setPendingSuiteId] = useState("");
  const [activeSuites, setActiveSuites] = useState([]);
  const [suitesLoading, setSuitesLoading] = useState(false);
  const [suitesError, setSuitesError] = useState("");

  // Add-time-window affordance state
  const [addWindowOpen, setAddWindowOpen] = useState(false);
  const [pendingDays, setPendingDays] = useState([]);
  const [pendingStart, setPendingStart] = useState("09:00");
  const [pendingEnd, setPendingEnd] = useState("12:00");
  const [windowError, setWindowError] = useState("");

  // Confirmation modal state
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Dirty-state guard
  const [discardPromptOpen, setDiscardPromptOpen] = useState(false);
  const initialStateRef = useRef(null);
  const dialogRef = useRef(null);

  // BUG-BR-003 — In-flight guard for the active-suites fetch. A ref (not
  // state) so toggling it does NOT re-trigger the effect dep-array — that
  // would cancel the pending Promise via the effect's cleanup before its
  // result lands. Used to dedupe between the eager-fetch trigger and the
  // suite-picker-open trigger when both fire before the first Promise
  // resolves.
  const activeSuitesFetchInFlightRef = useRef(false);

  // Initialize working state from the loaded sub-doc.
  //
  // BUG-BR-003 — `bookingRestrictions.blockedSuiteIds` arrives from
  // GET /api/staff/customers/:id as RAW ObjectIds (strings), NOT populated
  // suite refs. The dedicated GET .../booking-restrictions endpoint does
  // populate, but the parent (BookingRestrictionsCard / CustomerProfile)
  // sources from the customer-detail loader. Rather than change the parent
  // contract, we keep the raw shape in working state and enrich at render
  // time via cross-reference against `activeSuites` (which we now eagerly
  // fetch on modal-open when there are pre-existing blocked suites — see
  // the eager-fetch effect below).
  //
  // Each working-state row stores at minimum `{ _id }`. When the BE-supplied
  // entry happens to be a populated object (e.g. from a future migration to
  // the dedicated endpoint, or from a freshly-added entry via the suite
  // picker which carries the full Suite shape), its richer fields are kept
  // and used as a fallback for inactive/archived suites that won't appear
  // in `activeSuites`.
  useEffect(() => {
    if (!isOpen) return;
    if (bookingRestrictions) {
      const suiteRows = Array.isArray(bookingRestrictions.blockedSuiteIds)
        ? bookingRestrictions.blockedSuiteIds.map((s) => {
            // Tolerate three input shapes: raw ObjectId string, populated
            // object with `_id`, or legacy `{ id }` form.
            if (s == null) return { _id: "" };
            if (typeof s === "string") {
              return { _id: s };
            }
            return {
              _id: s._id || s.id,
              type: s.type,
              number: s.number,
              name: s.name,
              status: s.status || "active",
            };
          })
        : [];
      const windowRows = Array.isArray(bookingRestrictions.blockedTimeWindows)
        ? bookingRestrictions.blockedTimeWindows.map((w) => ({
            daysOfWeek: Array.isArray(w.daysOfWeek) ? [...w.daysOfWeek] : [],
            startTime: w.startTime || "",
            endTime: w.endTime || "",
          }))
        : [];
      setBlockedSuiteIds(suiteRows);
      setBlockedTimeWindows(windowRows);
      const eu = bookingRestrictions.effectiveUntil || null;
      setIsIndefinite(eu === null);
      setEffectiveUntil(
        eu ? moment.tz(eu, PHOENIX_TZ).format("YYYY-MM-DD") : ""
      );
      setReason(bookingRestrictions.reason || "");
      initialStateRef.current = {
        suites: suiteRows,
        windows: windowRows,
        indefinite: eu === null,
        until: eu ? moment.tz(eu, PHOENIX_TZ).format("YYYY-MM-DD") : "",
        reason: bookingRestrictions.reason || "",
      };
    } else {
      setBlockedSuiteIds([]);
      setBlockedTimeWindows([]);
      setIsIndefinite(true);
      setEffectiveUntil("");
      setReason("");
      initialStateRef.current = {
        suites: [],
        windows: [],
        indefinite: true,
        until: "",
        reason: "",
      };
    }
    // Reset transient affordance state
    setAddSuiteOpen(false);
    setPendingSuiteId("");
    setAddWindowOpen(false);
    setPendingDays([]);
    setPendingStart("09:00");
    setPendingEnd("12:00");
    setWindowError("");
    setConfirmSaveOpen(false);
    setConfirmRemoveOpen(false);
    setDiscardPromptOpen(false);
    setSaveError("");
    // BUG-BR-003 — release the in-flight latch on re-open so the fetch
    // can re-run if the prior open closed mid-flight. The eager-fetch
    // effect's idempotency gate covers the case where data already
    // arrived (activeSuites populated).
    activeSuitesFetchInFlightRef.current = false;
  }, [isOpen, bookingRestrictions]);

  // Fetch active suites when EITHER (a) the suite picker is opened, OR (b)
  // the modal opens with pre-existing blocked suites that need name/number
  // enrichment for the BLOCKED SUITES list (BUG-BR-003).
  //
  // The customer-detail endpoint returns `blockedSuiteIds` as raw ObjectIds;
  // the active-suites endpoint returns the full Suite shape. We cross-
  // reference at render time. Without this eager fetch, the rows would
  // render generic "Suite" labels until the admin happened to open the
  // picker. The fetch is idempotent (guarded by activeSuites.length and
  // suitesError) so opening the picker after the eager fetch is a no-op.
  const needsSuiteEnrichment =
    isOpen && blockedSuiteIds.length > 0;
  // Track unmount/close so an in-flight fetch can short-circuit its state
  // updates without depending on effect-cleanup semantics. The cleanup
  // function in the effect below ONLY fires on unmount or when `isOpen`
  // toggles false — NOT on every dep-array change — because dep-array
  // re-runs (e.g. `addSuiteOpen` flipping) must not abort an already-
  // running fetch (its result is still desired).
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (!addSuiteOpen && !needsSuiteEnrichment) return undefined;
    if (
      activeSuites.length > 0 ||
      suitesError ||
      activeSuitesFetchInFlightRef.current
    ) {
      return undefined;
    }
    activeSuitesFetchInFlightRef.current = true;
    setSuitesLoading(true);
    (async () => {
      try {
        const response = await staffAPI.getActiveSuites();
        if (!aliveRef.current) return;
        const list = Array.isArray(response?.data?.suites)
          ? response.data.suites
          : Array.isArray(response?.data)
          ? response.data
          : [];
        setActiveSuites(list);
        setSuitesError("");
      } catch (err) {
        if (!aliveRef.current) return;
        setSuitesError(
          "Couldn't load active suites. The suite list endpoint may not yet be available."
        );
      } finally {
        activeSuitesFetchInFlightRef.current = false;
        if (aliveRef.current) {
          setSuitesLoading(false);
        }
      }
    })();
    return undefined;
  }, [
    isOpen,
    addSuiteOpen,
    needsSuiteEnrichment,
    activeSuites.length,
    suitesError,
  ]);

  // Dirty detection
  const isDirty = useMemo(() => {
    const initial = initialStateRef.current;
    if (!initial) return false;
    if (initial.indefinite !== isIndefinite) return true;
    if (initial.until !== effectiveUntil) return true;
    if (initial.reason !== reason) return true;
    if (initial.suites.length !== blockedSuiteIds.length) return true;
    if (initial.windows.length !== blockedTimeWindows.length) return true;
    const initialIds = new Set(initial.suites.map((s) => s._id));
    const currentIds = new Set(blockedSuiteIds.map((s) => s._id));
    if (initialIds.size !== currentIds.size) return true;
    for (const id of initialIds) if (!currentIds.has(id)) return true;
    // Compare time windows by structural deep-equal
    const stringify = (w) =>
      JSON.stringify({
        d: [...(w.daysOfWeek || [])].sort((a, b) => a - b),
        s: w.startTime,
        e: w.endTime,
      });
    const initialKeys = initial.windows.map(stringify).sort();
    const currentKeys = blockedTimeWindows.map(stringify).sort();
    for (let i = 0; i < initialKeys.length; i += 1) {
      if (initialKeys[i] !== currentKeys[i]) return true;
    }
    return false;
  }, [
    blockedSuiteIds,
    blockedTimeWindows,
    isIndefinite,
    effectiveUntil,
    reason,
  ]);

  // Suite picker filter — exclude already-blocked
  const blockedSuiteIdSet = useMemo(
    () => new Set(blockedSuiteIds.map((s) => String(s._id))),
    [blockedSuiteIds]
  );

  // BUG-BR-003 — Enrich blocked-suite rows for display by cross-referencing
  // each working-state entry against `activeSuites`. Working state holds the
  // raw shape from the customer-detail endpoint (often just `{ _id }` since
  // `blockedSuiteIds` arrives as un-populated ObjectIds); display needs
  // `type`, `number`, `name` for `formatSuiteIdentity` to render "Sauna 5"
  // instead of a generic "Suite" placeholder.
  //
  // Resolution priority per row:
  //   1. Match in `activeSuites` by `_id` -> use the active-suite shape.
  //   2. Otherwise fall back to whatever fields the BE-supplied row has
  //      (covers archived suites that won't appear in `activeSuites`, and
  //      freshly-picked rows that already carry the full shape).
  //   3. If neither (1) nor (2) yields a `type` and we're past the load
  //      cycle (`suitesLoading=false`), surface the row with status
  //      "archived" so admin still sees + can remove it. Never silently
  //      drop the row regardless of state.
  const displayedBlockedSuites = useMemo(() => {
    return blockedSuiteIds.map((row) => {
      const idStr = String(row._id || "");
      const match = activeSuites.find((s) => String(s._id) === idStr);
      if (match) {
        return {
          _id: match._id,
          type: match.type,
          number: match.number,
          name: match.name,
          status: match.status || "active",
        };
      }
      const hasUsableFields = row.type != null || row.number != null;
      // If the row carries fields, trust its own status. Otherwise mark
      // archived ONLY after the active-suites load has settled — while
      // loading, leave status as-is so the row simply renders the generic
      // formatter without a misleading [Inactive suite] tag.
      let status = row.status || "active";
      if (!hasUsableFields && !suitesLoading && activeSuites.length > 0) {
        status = "archived";
      }
      return {
        _id: row._id,
        type: row.type,
        number: row.number,
        name: row.name,
        status,
      };
    });
  }, [blockedSuiteIds, activeSuites, suitesLoading]);

  const groupedSuiteOptions = useMemo(() => {
    const groups = {};
    activeSuites
      .filter((s) => !blockedSuiteIdSet.has(String(s._id)))
      .forEach((s) => {
        const typeKey = String(s.type || "").toLowerCase().replace(/\s+/g, "_");
        const typeLabel = SUITE_TYPE_LABELS[typeKey] || s.type || "Suite";
        if (!groups[typeLabel]) groups[typeLabel] = [];
        groups[typeLabel].push(s);
      });
    Object.keys(groups).forEach((k) =>
      groups[k].sort((a, b) => (a.number || 0) - (b.number || 0))
    );
    return groups;
  }, [activeSuites, blockedSuiteIdSet]);

  const allActiveBlocked =
    activeSuites.length > 0 &&
    activeSuites.every((s) => blockedSuiteIdSet.has(String(s._id)));

  const hasAnyRules =
    blockedSuiteIds.length > 0 || blockedTimeWindows.length > 0;
  const hasExistingProfile = Boolean(bookingRestrictions);

  // Past-date warning per Zuri §4.6
  const pastDateWarning = useMemo(() => {
    if (isIndefinite || !effectiveUntil) return false;
    const target = moment.tz(effectiveUntil, "YYYY-MM-DD", PHOENIX_TZ);
    const now = moment.tz(PHOENIX_TZ).startOf("day");
    return target.isValid() && !target.isAfter(now);
  }, [isIndefinite, effectiveUntil]);

  const handleAttemptClose = useCallback(() => {
    if (saving) return;
    if (isDirty) {
      setDiscardPromptOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, saving, onClose]);

  // Escape-key dismissal — works for editor (with dirty guard) and for
  // confirmation overlays (which intercept first because they're rendered
  // later in the DOM order)
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (e.key !== "Escape") return;
      if (confirmSaveOpen) {
        setConfirmSaveOpen(false);
        return;
      }
      if (confirmRemoveOpen) {
        setConfirmRemoveOpen(false);
        return;
      }
      if (discardPromptOpen) {
        setDiscardPromptOpen(false);
        return;
      }
      handleAttemptClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [
    isOpen,
    confirmSaveOpen,
    confirmRemoveOpen,
    discardPromptOpen,
    handleAttemptClose,
  ]);

  if (!isOpen) return null;

  const customerName = customer
    ? `${customer.firstName || ""} ${customer.lastName || ""}`.trim() || "this customer"
    : "this customer";
  const customerEmail = customer?.email || "";

  const handleRemoveSuite = (id) => {
    setBlockedSuiteIds((prev) => prev.filter((s) => String(s._id) !== String(id)));
  };

  const handleAddSuite = () => {
    if (!pendingSuiteId) return;
    const found = activeSuites.find(
      (s) => String(s._id) === String(pendingSuiteId)
    );
    if (!found) return;
    setBlockedSuiteIds((prev) => [...prev, found]);
    setPendingSuiteId("");
    setAddSuiteOpen(false);
  };

  const handleToggleDay = (idx) => {
    setPendingDays((prev) =>
      prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx]
    );
    setWindowError("");
  };

  const handleAddTimeWindow = () => {
    const validation = validateTimeWindow(
      {
        daysOfWeek: pendingDays,
        startTime: pendingStart,
        endTime: pendingEnd,
      },
      blockedTimeWindows
    );
    if (!validation.valid) {
      setWindowError(validation.error);
      return;
    }
    setBlockedTimeWindows((prev) => [
      ...prev,
      {
        daysOfWeek: [...pendingDays].sort((a, b) => a - b),
        startTime: pendingStart,
        endTime: pendingEnd,
      },
    ]);
    setPendingDays([]);
    setPendingStart("09:00");
    setPendingEnd("12:00");
    setWindowError("");
    setAddWindowOpen(false);
  };

  const handleRemoveWindow = (index) => {
    setBlockedTimeWindows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveClick = () => {
    if (!hasAnyRules) return;
    setSaveError("");
    setConfirmSaveOpen(true);
  };

  const buildPutPayload = () => ({
    blockedSuiteIds: blockedSuiteIds.map((s) => s._id),
    blockedTimeWindows: blockedTimeWindows.map((w) => ({
      daysOfWeek: [...w.daysOfWeek].sort((a, b) => a - b),
      startTime: w.startTime,
      endTime: w.endTime,
    })),
    effectiveUntil: isIndefinite ? null : effectiveUntil || null,
    reason: reason || "",
  });

  const handleConfirmSave = async () => {
    setSaving(true);
    setSaveError("");
    try {
      await staffAPI.setBookingRestrictions(customerId, buildPutPayload());
      setSaving(false);
      setConfirmSaveOpen(false);
      toast.success("Restrictions saved");
      if (typeof onSaved === "function") onSaved();
      onClose();
    } catch (err) {
      setSaving(false);
      const message =
        err?.response?.data?.message ||
        (err?.response?.status >= 500 || !err?.response
          ? "Couldn't reach the server. Try again."
          : "Couldn't save restrictions.");
      setSaveError(message);
    }
  };

  const handleConfirmRemove = async () => {
    setSaving(true);
    setSaveError("");
    try {
      await staffAPI.removeBookingRestrictions(customerId);
      setSaving(false);
      setConfirmRemoveOpen(false);
      toast.success("All restrictions removed");
      if (typeof onSaved === "function") onSaved();
      onClose();
    } catch (err) {
      setSaving(false);
      const message =
        err?.response?.data?.message ||
        (err?.response?.status >= 500 || !err?.response
          ? "Couldn't reach the server. Try again."
          : "Couldn't remove restrictions.");
      setSaveError(message);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleAttemptClose();
    }
  };

  const setByName = bookingRestrictions?.setBy
    ? `${bookingRestrictions.setBy.firstName || ""} ${
        bookingRestrictions.setBy.lastName || ""
      }`.trim() || bookingRestrictions.setBy.email
    : null;
  const setAtFormatted = formatProvenanceTimestamp(bookingRestrictions?.setAt);
  const lastModifiedFormatted = formatProvenanceTimestamp(
    bookingRestrictions?.lastModifiedAt
  );

  return (
    <div
      className="booking-restrictions-modal-overlay"
      role="presentation"
      onMouseDown={handleBackdropClick}
      data-testid="booking-restrictions-modal-overlay"
    >
      <div
        className="booking-restrictions-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-restrictions-modal-title"
        ref={dialogRef}
        data-testid="booking-restrictions-modal"
      >
        <div className="booking-restrictions-modal__header">
          <h2
            id="booking-restrictions-modal-title"
            className="booking-restrictions-modal__title"
          >
            Booking Restrictions — {customerName}
          </h2>
          <button
            type="button"
            className="booking-restrictions-modal__close"
            aria-label="Close booking restrictions"
            onClick={handleAttemptClose}
            disabled={saving}
            data-testid="booking-restrictions-modal-close"
          >
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
              <path
                d="M3 3l10 10M13 3L3 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="booking-restrictions-modal__body">
          {!hasExistingProfile && !hasAnyRules ? (
            <div
              className="booking-restrictions-modal__empty-nudge"
              data-testid="booking-restrictions-modal-empty-nudge"
            >
              <span
                className="booking-restrictions-modal__empty-icon"
                aria-hidden="true"
              >
                <svg viewBox="0 0 32 32" width="32" height="32">
                  <path
                    d="M16 3l11 5v7c0 7-5 12-11 14-6-2-11-7-11-14V8l11-5z"
                    fill="currentColor"
                  />
                </svg>
              </span>
              <p className="booking-restrictions-modal__empty-copy">
                No restrictions yet. Use this surface to block specific suites or recurring weekly times. Customers do not see that they are restricted &mdash; restricted slots simply do not appear in their availability.
              </p>
            </div>
          ) : null}

          {/* BLOCKED SUITES section */}
          <section
            className="booking-restrictions-modal__section"
            aria-labelledby="booking-restrictions-section-suites"
          >
            <h3
              id="booking-restrictions-section-suites"
              className="booking-restrictions-modal__section-label"
            >
              BLOCKED SUITES
            </h3>
            {displayedBlockedSuites.length === 0 ? (
              <p className="booking-restrictions-modal__empty-row">
                No suites blocked yet.
              </p>
            ) : (
              <ul
                className="booking-restrictions-modal__list"
                data-testid="booking-restrictions-modal-suite-list"
              >
                {displayedBlockedSuites.map((suite) => {
                  const inactive =
                    suite.status && suite.status !== "active";
                  return (
                    <li
                      key={String(suite._id)}
                      className="booking-restrictions-modal__row"
                    >
                      <span className="booking-restrictions-modal__row-icon" aria-hidden="true">
                        <svg viewBox="0 0 18 18" width="18" height="18">
                          <rect x="3" y="2" width="12" height="14" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                          <circle cx="12" cy="9.5" r="0.8" fill="currentColor" />
                        </svg>
                      </span>
                      <span className="booking-restrictions-modal__row-identity">
                        {formatSuiteIdentity(suite)}
                        {inactive ? (
                          <span
                            className="booking-restrictions-modal__inactive-tag"
                            data-testid="booking-restrictions-modal-inactive-tag"
                          >
                            [Inactive suite]
                          </span>
                        ) : null}
                      </span>
                      <button
                        type="button"
                        className="booking-restrictions-modal__row-remove"
                        aria-label={`Remove suite block on ${formatSuiteIdentity(suite)}`}
                        onClick={() => handleRemoveSuite(suite._id)}
                      >
                        Remove
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {addSuiteOpen ? (
              <div
                className="booking-restrictions-modal__inline-form"
                data-testid="booking-restrictions-modal-suite-picker"
              >
                <p className="booking-restrictions-modal__inline-prompt">
                  Block which suite?
                </p>
                {suitesLoading ? (
                  <p className="booking-restrictions-modal__inline-helper">
                    Loading active suites&hellip;
                  </p>
                ) : suitesError ? (
                  <p className="booking-restrictions-modal__inline-error">
                    {suitesError}
                  </p>
                ) : allActiveBlocked ? (
                  <p className="booking-restrictions-modal__inline-helper">
                    All available suites are already blocked.
                  </p>
                ) : (
                  <select
                    className="booking-restrictions-modal__select"
                    value={pendingSuiteId}
                    onChange={(e) => setPendingSuiteId(e.target.value)}
                    aria-label="Choose a suite to block"
                    data-testid="booking-restrictions-modal-suite-select"
                  >
                    <option value="">Select a suite</option>
                    {Object.keys(groupedSuiteOptions)
                      .sort()
                      .map((typeLabel) => (
                        <optgroup key={typeLabel} label={typeLabel}>
                          {groupedSuiteOptions[typeLabel].map((s) => (
                            <option key={String(s._id)} value={String(s._id)}>
                              {formatSuiteIdentity(s)}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                  </select>
                )}
                <div className="booking-restrictions-modal__inline-actions">
                  <button
                    type="button"
                    className="booking-restrictions-modal__btn booking-restrictions-modal__btn--outlined"
                    onClick={() => {
                      setAddSuiteOpen(false);
                      setPendingSuiteId("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="booking-restrictions-modal__btn booking-restrictions-modal__btn--filled"
                    disabled={!pendingSuiteId}
                    onClick={handleAddSuite}
                    data-testid="booking-restrictions-modal-suite-add"
                  >
                    Add suite
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="booking-restrictions-modal__add-cta"
                onClick={() => setAddSuiteOpen(true)}
                data-testid="booking-restrictions-modal-suite-open"
              >
                {blockedSuiteIds.length === 0
                  ? "+ Block a suite"
                  : "+ Block another suite"}
              </button>
            )}
          </section>

          {/* BLOCKED TIME WINDOWS section */}
          <section
            className="booking-restrictions-modal__section"
            aria-labelledby="booking-restrictions-section-windows"
          >
            <h3
              id="booking-restrictions-section-windows"
              className="booking-restrictions-modal__section-label"
            >
              BLOCKED TIME WINDOWS
            </h3>
            {blockedTimeWindows.length === 0 ? (
              <p className="booking-restrictions-modal__empty-row">
                No time windows blocked yet.
              </p>
            ) : (
              <ul
                className="booking-restrictions-modal__list"
                data-testid="booking-restrictions-modal-window-list"
              >
                {blockedTimeWindows.map((win, idx) => (
                  <li
                    key={`${win.startTime}-${win.endTime}-${idx}`}
                    className="booking-restrictions-modal__row booking-restrictions-modal__row--window"
                  >
                    <span className="booking-restrictions-modal__row-icon" aria-hidden="true">
                      <svg viewBox="0 0 18 18" width="18" height="18">
                        <circle cx="9" cy="9" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M9 4.5V9l3 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                      </svg>
                    </span>
                    <span className="booking-restrictions-modal__row-identity">
                      <span>
                        {formatDaysOfWeek(win.daysOfWeek)} &middot; {win.startTime} &ndash; {win.endTime} MST
                      </span>
                      <span className="booking-restrictions-modal__row-helper">
                        Blocks slots starting from {win.startTime} up to (but not including) {win.endTime} MST.
                      </span>
                    </span>
                    <button
                      type="button"
                      className="booking-restrictions-modal__row-remove"
                      aria-label={`Remove time window block on ${formatDaysOfWeek(win.daysOfWeek)} from ${win.startTime} to ${win.endTime} MST`}
                      onClick={() => handleRemoveWindow(idx)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {addWindowOpen ? (
              <div
                className="booking-restrictions-modal__inline-form"
                data-testid="booking-restrictions-modal-window-picker"
              >
                <p className="booking-restrictions-modal__inline-prompt">
                  Block which time window?
                </p>
                <fieldset className="booking-restrictions-modal__fieldset">
                  <legend className="booking-restrictions-modal__fieldset-label">
                    Days of the week
                  </legend>
                  <div
                    role="group"
                    aria-label="Days of the week"
                    className="booking-restrictions-modal__chips"
                  >
                    {DAY_LETTERS.map((letter, idx) => {
                      const active = pendingDays.includes(idx);
                      return (
                        <button
                          key={`${idx}-${DAY_NAMES[idx]}`}
                          type="button"
                          role="checkbox"
                          aria-checked={active}
                          aria-label={DAY_NAMES[idx]}
                          className={
                            "booking-restrictions-modal__chip" +
                            (active ? " booking-restrictions-modal__chip--active" : "")
                          }
                          onClick={() => handleToggleDay(idx)}
                          data-testid={`booking-restrictions-modal-day-${idx}`}
                        >
                          {letter}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
                <div className="booking-restrictions-modal__time-row">
                  <label className="booking-restrictions-modal__time-label">
                    Start
                    <input
                      type="time"
                      step="900"
                      value={pendingStart}
                      onChange={(e) => {
                        setPendingStart(e.target.value);
                        setWindowError("");
                      }}
                      aria-label="Start time (MST)"
                      data-testid="booking-restrictions-modal-window-start"
                    />
                  </label>
                  <label className="booking-restrictions-modal__time-label">
                    End
                    <input
                      type="time"
                      step="900"
                      value={pendingEnd}
                      onChange={(e) => {
                        setPendingEnd(e.target.value);
                        setWindowError("");
                      }}
                      aria-label="End time (MST)"
                      data-testid="booking-restrictions-modal-window-end"
                    />
                  </label>
                </div>
                <p className="booking-restrictions-modal__inline-helper">
                  Blocks slots starting from start time up to (but not including) end time. Times in MST.
                </p>
                <p className="booking-restrictions-modal__inline-helper">
                  For overnight rules (across midnight), add two windows (e.g., Sun 22:00&ndash;23:59 + Mon 00:00&ndash;02:00).
                </p>
                {windowError ? (
                  <p
                    className="booking-restrictions-modal__inline-error"
                    role="alert"
                    data-testid="booking-restrictions-modal-window-error"
                  >
                    {windowError}
                  </p>
                ) : null}
                <div className="booking-restrictions-modal__inline-actions">
                  <button
                    type="button"
                    className="booking-restrictions-modal__btn booking-restrictions-modal__btn--outlined"
                    onClick={() => {
                      setAddWindowOpen(false);
                      setPendingDays([]);
                      setPendingStart("09:00");
                      setPendingEnd("12:00");
                      setWindowError("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="booking-restrictions-modal__btn booking-restrictions-modal__btn--filled"
                    disabled={Boolean(windowError) || pendingDays.length === 0}
                    onClick={handleAddTimeWindow}
                    data-testid="booking-restrictions-modal-window-add"
                  >
                    Add time window
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="booking-restrictions-modal__add-cta"
                onClick={() => setAddWindowOpen(true)}
                data-testid="booking-restrictions-modal-window-open"
              >
                {blockedTimeWindows.length === 0
                  ? "+ Block a time window"
                  : "+ Block another time window"}
              </button>
            )}
          </section>

          {/* EFFECTIVE UNTIL section */}
          <section
            className="booking-restrictions-modal__section"
            aria-labelledby="booking-restrictions-section-effective"
          >
            <h3
              id="booking-restrictions-section-effective"
              className="booking-restrictions-modal__section-label"
            >
              EFFECTIVE UNTIL
            </h3>
            <div
              role="radiogroup"
              aria-label="When the restriction takes effect until"
              className="booking-restrictions-modal__radiogroup"
            >
              <label className="booking-restrictions-modal__radio">
                <input
                  type="radio"
                  name="effective-until-mode"
                  checked={isIndefinite}
                  onChange={() => setIsIndefinite(true)}
                  data-testid="booking-restrictions-modal-indefinite"
                />
                <span>Indefinite</span>
              </label>
              <label className="booking-restrictions-modal__radio">
                <input
                  type="radio"
                  name="effective-until-mode"
                  checked={!isIndefinite}
                  onChange={() => setIsIndefinite(false)}
                  data-testid="booking-restrictions-modal-until-date"
                />
                <span>Until specific date</span>
                <input
                  type="date"
                  className="booking-restrictions-modal__date"
                  value={effectiveUntil}
                  onChange={(e) => setEffectiveUntil(e.target.value)}
                  disabled={isIndefinite}
                  aria-label="Effective until date (MST)"
                  data-testid="booking-restrictions-modal-until-date-input"
                />
              </label>
            </div>
            <p className="booking-restrictions-modal__inline-helper">
              Restriction stops being enforced at midnight on this date, MST.
            </p>
            {pastDateWarning ? (
              <p className="booking-restrictions-modal__warn">
                This date is in the past &mdash; the restriction will not be enforced.
              </p>
            ) : null}
          </section>

          {/* REASON section */}
          <section
            className="booking-restrictions-modal__section"
            aria-labelledby="booking-restrictions-section-reason"
          >
            <h3
              id="booking-restrictions-section-reason"
              className="booking-restrictions-modal__section-label"
            >
              REASON <span className="booking-restrictions-modal__section-label-aux">(optional &mdash; staff visibility only)</span>
            </h3>
            <label
              htmlFor="booking-restrictions-modal-reason-input"
              className="booking-restrictions-modal__sr-only"
            >
              Reason (optional &mdash; staff visibility only)
            </label>
            <textarea
              id="booking-restrictions-modal-reason-input"
              className="booking-restrictions-modal__textarea"
              rows={3}
              maxLength={REASON_MAX_LENGTH}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              aria-describedby="booking-restrictions-modal-reason-helper"
              data-testid="booking-restrictions-modal-reason"
            />
            <p
              id="booking-restrictions-modal-reason-helper"
              className="booking-restrictions-modal__inline-helper"
            >
              Visible only to staff. Not shown to the customer.
            </p>
            {reason.length > REASON_COUNTER_THRESHOLD ? (
              <p className="booking-restrictions-modal__counter">
                {REASON_MAX_LENGTH - reason.length} characters remaining
              </p>
            ) : null}
          </section>

          {(setByName || lastModifiedFormatted) ? (
            <div
              className="booking-restrictions-modal__provenance"
              data-testid="booking-restrictions-modal-provenance"
            >
              {setByName && setAtFormatted ? (
                <p>
                  Set by {setByName} &middot; {setAtFormatted} MST
                </p>
              ) : null}
              {lastModifiedFormatted ? (
                <p>Last modified {lastModifiedFormatted} MST</p>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="booking-restrictions-modal__footer">
          {hasExistingProfile ? (
            <button
              type="button"
              className="booking-restrictions-modal__btn booking-restrictions-modal__btn--destructive"
              onClick={() => setConfirmRemoveOpen(true)}
              disabled={saving}
              data-testid="booking-restrictions-modal-remove-all"
            >
              Remove all restrictions
            </button>
          ) : (
            <span />
          )}
          <div className="booking-restrictions-modal__footer-actions">
            <button
              type="button"
              className="booking-restrictions-modal__btn booking-restrictions-modal__btn--outlined"
              onClick={handleAttemptClose}
              disabled={saving}
              data-testid="booking-restrictions-modal-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              className="booking-restrictions-modal__btn booking-restrictions-modal__btn--filled"
              disabled={!hasAnyRules || saving}
              onClick={handleSaveClick}
              data-testid="booking-restrictions-modal-save"
              aria-label={saving ? "Saving restriction profile" : "Save restriction profile"}
            >
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Save confirmation (Zuri §4.8) */}
      {confirmSaveOpen ? (
        <div
          className="booking-restrictions-modal-overlay booking-restrictions-modal-overlay--nested"
          role="presentation"
          data-testid="booking-restrictions-modal-confirm-save-overlay"
        >
          <div
            className="booking-restrictions-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="booking-restrictions-confirm-save-title"
            data-testid="booking-restrictions-modal-confirm-save"
          >
            <h2
              id="booking-restrictions-confirm-save-title"
              className="booking-restrictions-confirm__title"
            >
              Confirm restrictions for {customerName}?
            </h2>
            <p className="booking-restrictions-confirm__customer">
              {customerName}
              {customerEmail ? ` (${customerEmail})` : ""}
            </p>
            <p className="booking-restrictions-confirm__will-block-label">
              Will block:
            </p>
            <ul className="booking-restrictions-confirm__will-block">
              {displayedBlockedSuites.map((s) => (
                <li key={`will-suite-${String(s._id)}`}>
                  {formatSuiteIdentity(s)}
                </li>
              ))}
              {blockedTimeWindows.map((w, idx) => (
                <li key={`will-window-${idx}`}>
                  {formatDaysOfWeek(w.daysOfWeek)} &middot; {w.startTime} &ndash; {w.endTime} MST
                </li>
              ))}
            </ul>
            <p className="booking-restrictions-confirm__effective">
              Effective until:{" "}
              {isIndefinite
                ? "Indefinite"
                : formatEffectiveUntilDisplay(effectiveUntil)}
            </p>
            <p className="booking-restrictions-confirm__reason">
              Reason: {reason ? `"${reason}"` : "—"}
            </p>
            <p className="booking-restrictions-confirm__guardrail">
              This customer will not be able to book the listed suites or time windows. They will not be told they have been restricted &mdash; the slots will simply not appear in their availability.
            </p>
            {saveError ? (
              <p
                className="booking-restrictions-modal__inline-error"
                role="alert"
              >
                Couldn&rsquo;t save: {saveError}
              </p>
            ) : null}
            <div className="booking-restrictions-confirm__actions">
              <button
                type="button"
                className="booking-restrictions-modal__btn booking-restrictions-modal__btn--outlined"
                onClick={() => {
                  if (!saving) setConfirmSaveOpen(false);
                }}
                disabled={saving}
                data-testid="booking-restrictions-modal-confirm-save-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                className="booking-restrictions-modal__btn booking-restrictions-modal__btn--filled"
                onClick={handleConfirmSave}
                disabled={saving}
                data-testid="booking-restrictions-modal-confirm-save-confirm"
              >
                {saving ? "Saving…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Remove-all confirmation (Zuri §4.10) */}
      {confirmRemoveOpen ? (
        <div
          className="booking-restrictions-modal-overlay booking-restrictions-modal-overlay--nested"
          role="presentation"
          data-testid="booking-restrictions-modal-confirm-remove-overlay"
        >
          <div
            className="booking-restrictions-confirm booking-restrictions-confirm--destructive"
            role="dialog"
            aria-modal="true"
            aria-labelledby="booking-restrictions-confirm-remove-title"
            data-testid="booking-restrictions-modal-confirm-remove"
          >
            <h2
              id="booking-restrictions-confirm-remove-title"
              className="booking-restrictions-confirm__title"
            >
              Remove all restrictions for {customerName}?
            </h2>
            <p>
              This will allow {customerName} to book all suites and at all times again.
            </p>
            <p>
              {blockedSuiteIds.length} blocked suite{blockedSuiteIds.length === 1 ? "" : "s"} and {blockedTimeWindows.length} blocked time window{blockedTimeWindows.length === 1 ? "" : "s"} will be removed from their account.
            </p>
            <p className="booking-restrictions-confirm__audit">
              The removal will be recorded in the audit log.
            </p>
            {saveError ? (
              <p
                className="booking-restrictions-modal__inline-error"
                role="alert"
              >
                {saveError}
              </p>
            ) : null}
            <div className="booking-restrictions-confirm__actions">
              <button
                type="button"
                className="booking-restrictions-modal__btn booking-restrictions-modal__btn--outlined"
                onClick={() => {
                  if (!saving) setConfirmRemoveOpen(false);
                }}
                disabled={saving}
                data-testid="booking-restrictions-modal-confirm-remove-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                className="booking-restrictions-modal__btn booking-restrictions-modal__btn--destructive-filled"
                onClick={handleConfirmRemove}
                disabled={saving}
                data-testid="booking-restrictions-modal-confirm-remove-confirm"
              >
                {saving ? "Removing…" : "Remove all"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Discard-changes prompt (Zuri §4.9) */}
      {discardPromptOpen ? (
        <div
          className="booking-restrictions-modal-overlay booking-restrictions-modal-overlay--nested"
          role="presentation"
          data-testid="booking-restrictions-modal-discard-overlay"
        >
          <div
            className="booking-restrictions-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="booking-restrictions-confirm-discard-title"
          >
            <h2
              id="booking-restrictions-confirm-discard-title"
              className="booking-restrictions-confirm__title"
            >
              Discard unsaved changes?
            </h2>
            <div className="booking-restrictions-confirm__actions">
              <button
                type="button"
                className="booking-restrictions-modal__btn booking-restrictions-modal__btn--outlined"
                onClick={() => setDiscardPromptOpen(false)}
                data-testid="booking-restrictions-modal-discard-keep"
              >
                Keep editing
              </button>
              <button
                type="button"
                className="booking-restrictions-modal__btn booking-restrictions-modal__btn--destructive-filled"
                onClick={() => {
                  setDiscardPromptOpen(false);
                  onClose();
                }}
                data-testid="booking-restrictions-modal-discard-confirm"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BookingRestrictionsModal;
